import { useEffect, useRef, useCallback } from 'react'

const INACTIVITY_TIMEOUT = 15 * 60 * 1000 // 15 minutes in milliseconds
const DEBUG_INACTIVITY =
  process.env.NODE_ENV === 'development' || process.env.DEBUG_INACTIVITY === 'true'

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'mouseup',
  'keydown',
  'keyup',
  'scroll',
  'touchstart',
  'touchend',
  'click',
  'focus',
  'blur',
] as const

export function useInactivityLogout(signout: () => void, isAuthenticated: boolean) {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const isAuthenticatedRef = useRef(isAuthenticated)
  const signoutRef = useRef(signout)

  // Update refs when values change
  isAuthenticatedRef.current = isAuthenticated
  signoutRef.current = signout

  const resetTimeout = useCallback(() => {
    // Only reset timeout if user is authenticated
    if (!isAuthenticatedRef.current) return

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (DEBUG_INACTIVITY) {
      console.warn('Inactivity timeout reset - user activity detected')
    }

    timeoutRef.current = setTimeout(() => {
      // Double check auth status before logging out
      if (isAuthenticatedRef.current) {
        console.warn('Inactivity timeout reached, logging out user')
        signoutRef.current()
      }
    }, INACTIVITY_TIMEOUT)
  }, [])

  const clearInactivityTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Handle visibility change (tab becomes active/inactive)
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible' && isAuthenticatedRef.current) {
      // Tab became visible, reset timeout
      if (DEBUG_INACTIVITY) {
        console.warn('Tab became visible, resetting inactivity timeout')
      }
      resetTimeout()
    } else if (document.visibilityState === 'hidden') {
      // Tab became hidden, clear timeout to prevent logout while tab is hidden
      if (DEBUG_INACTIVITY) {
        console.warn('Tab became hidden, clearing inactivity timeout')
      }
      clearInactivityTimeout()
    }
  }, [resetTimeout, clearInactivityTimeout])

  useEffect(() => {
    if (!isAuthenticated) {
      if (DEBUG_INACTIVITY) {
        console.warn('User not authenticated, clearing inactivity timeout')
      }
      clearInactivityTimeout()
      return
    }

    if (DEBUG_INACTIVITY) {
      console.warn('User authenticated, starting inactivity timeout')
    }

    // Start the timeout when user is authenticated
    resetTimeout()

    // Add event listeners for activity detection
    ACTIVITY_EVENTS.forEach((eventName) => {
      document.addEventListener(eventName, resetTimeout, { passive: true })
    })

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup function
    return () => {
      if (DEBUG_INACTIVITY) {
        console.warn('Cleaning up inactivity timeout listeners')
      }
      clearInactivityTimeout()
      ACTIVITY_EVENTS.forEach((eventName) => {
        document.removeEventListener(eventName, resetTimeout)
      })
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [isAuthenticated, resetTimeout, clearInactivityTimeout, handleVisibilityChange])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearInactivityTimeout()
    }
  }, [clearInactivityTimeout])
}
